/**
 * HeroView for Poker Reborn
 *
 * Dedicated view for the hero player (the local player).
 * Uses a "split container" pattern for z-ordering:
 * - seatContainer → mounted in seatsLayer (avatar, info bar, timer, badges)
 * - cardsContainer → mounted in playersCardsLayer (hero cards + hand meter)
 *
 * This is the architectural foundation for the hero "cockpit" experience.
 * Currently renders 1:1 identical to SeatView, but will evolve with
 * specialized visuals, card layouts, and HUD elements.
 *
 * @see docs/screens/room/HERO_VIEW.md - Hero seat visual specs, architecture
 * @see docs/screens/room/HAND_STRENGTH_METER.md - Hand meter specs
 * @see docs/screens/room/CARDS_HERO.md - Hero card specs
 */

import { Assets, BlurFilter, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { PlayerInfoBar } from '@/components/table/PlayerInfoBar';
import { PlayerChipsDisplay } from '@/components/table/PlayerChipsDisplay';
import { TimerBorder } from '@/components/table/TimerBorder';
import { SitoutBadge } from '@/components/table/SitoutBadge';
import { HoleCardsView } from '@/components/cards/HoleCardsView';
import { HandStrengthMeter } from '@/components/table/HandStrengthMeter';
import type { CommunityCardsView } from '@/components/table/CommunityCardsView';
import type { PlayerState, Card, Street } from '@/types/game';
import { getScaledValue } from '@/utils/responsive';
import { DEFAULT_AVATAR_ID, getAvatarPath } from '@/config/avatars';
import { avatarService } from '@/services/AvatarService';
import { storageService, type StorageKey } from '@/services/StorageService';
import { gameController } from '@/controllers/GameController';
import { tableState } from '@/state/TableState';
import { getPreflopStrength, getPostflopStrength, getStrengthName } from '@/utils/handStrength';
import { detectDraws } from '@/utils/drawDetection';
import { HAND_STRENGTH_METER_OFFSET, HERO_SEAT_SIZE, HERO_SEAT_OFFSET, HERO_CARDS_OFFSET } from '@/config/cockpit';
import { log } from '@/utils/log';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';

// Alias for brevity
const SEAT_BREAKPOINTS = HERO_SEAT_SIZE;

// =============================================================================
// STYLES
// =============================================================================

const COLORS = {
  text: {
    name: 0xffffff,
    stack: 0xffd700,
  },
  winnerBadge: {
    fill: 0x4caf50,
    textFill: 0xffffff,
    padding: 6,
  },
  lastAction: {
    textFill: 0xffffff,
    fold: 0x666666,
    check: 0x2e7d32,
    call: 0x2e7d32,
    bet: 0xe67e00,
    raise: 0xe67e00,
    allin: 0xd32f2f,
    default: 0x555555,
    padding: { x: 8, y: 4 },
    cornerRadius: 4,
  },
};

// =============================================================================
// HERO SEAT VIEW
// =============================================================================

export class HeroView {
  /** Container for seat visuals — mount in seatsLayer */
  readonly seatContainer: Container;

  /** Container for hero cards + hand meter — mount in playersCardsLayer */
  readonly cardsContainer: Container;

  // Seat visual children (inside seatContainer)
  private playerContainer: Container;
  private avatarContainer: Container;
  private playerInfoBar: PlayerInfoBar;
  private timerBorder: TimerBorder;
  private sitoutBadge: SitoutBadge;
  private winnerBadge: Container;
  private avatarGlow: Graphics;
  private lastActContainer: Container;
  private lastActBg: Graphics;
  private lastActLabel: Text;

  // Avatar sprite state
  private avatarShadowSprite: Sprite | null = null;
  private avatarSprite: Sprite | null = null;
  private currentAvatarId: number | null = null;
  private avatarLoadGeneration = 0;

  // Cards children (inside cardsContainer)
  private holeCardsView: HoleCardsView;
  private handStrengthMeter: HandStrengthMeter;

  // State
  private player: PlayerState | null = null;
  private isActive = false;
  private currentScreenWidth = 1920;
  private isDestroyed = false;

  // T71: Draw prediction overlay reference
  private communityCardsViewRef: CommunityCardsView | null = null;

  // Event listener references for cleanup (arrow properties for proper off() binding)
  private onHoleCardsDealt = ({ cards }: { cards: Card[] }): void => {
    const cardLabels = cards.map((c) => `${c.rank}${c.suit}`).join(', ');
    log.table.debug`Hero cards received: ${cardLabels}`;

    if (storageService.get('settings.game.showHandStrength') ?? true) {
      // Preflop strength uses first two cards (Hold'em or Omaha)
      const firstCard = cards[0];
      const secondCard = cards[1];
      if (firstCard && secondCard) {
        const strength = getPreflopStrength(firstCard, secondCard);
        const strengthName = getStrengthName(strength, true);
        this.handStrengthMeter.setValue(strength, strengthName);
        this.updateMeterPosition();
        this.handStrengthMeter.visible = true;
      }
    }
  };

  private onStreetChanged = ({ street }: { street: Street }): void => {
    // T71: Update draw predictions
    this.updateDrawPredictions(street);

    if (street === 'showdown') return;

    const heroCards = tableState.heroCards.get();
    if (!heroCards) return;

    if (!(storageService.get('settings.game.showHandStrength') ?? true)) return;

    const board = tableState.communityCards.get();
    if (board.length < 3) return;

    const allCards = [...heroCards, ...board];
    const strength = getPostflopStrength(allCards);
    const handName = getStrengthName(strength);
    this.handStrengthMeter.setValue(strength, handName);
  };

  private onHandStarted = (): void => {
    this.handStrengthMeter.reset();
    this.handStrengthMeter.visible = false;
    this.communityCardsViewRef?.clearDrawPredictions();
  };

  private onStorageChange = ({ key, value }: { key: StorageKey; value: unknown; previousValue: unknown }): void => {
    if (key === 'settings.game.showHandStrength') {
      if (!value) {
        this.handStrengthMeter.visible = false;
      } else if (tableState.heroCards.get()) {
        this.handStrengthMeter.visible = true;
      }
    }
  };

  /**
   * Get a BBS value for current screen width (discrete mode).
   */
  private bbs(breakpoints: Record<number, number>): number {
    return getScaledValue(this.currentScreenWidth, breakpoints, { discrete: true });
  }

  constructor() {
    // =========================================================================
    // SEAT CONTAINER (avatar, info bar, timer, badges)
    // =========================================================================
    this.seatContainer = new Container();
    this.seatContainer.label = 'heroSeatContainer';
    this.seatContainer.scale.set(1.25);

    this.playerContainer = new Container();
    this.playerContainer.label = 'playerContainer';
    this.seatContainer.addChild(this.playerContainer);

    // Avatar container
    this.avatarContainer = new Container();
    this.playerContainer.addChild(this.avatarContainer);

    // Avatar glow (behind avatar, for aura effects)
    this.avatarGlow = new Graphics();
    this.avatarGlow.label = 'avatarGlow';
    this.avatarGlow.visible = false;
    this.avatarContainer.addChild(this.avatarGlow);

    // Player info bar (name + chips)
    this.playerInfoBar = new PlayerInfoBar({
      screenWidth: this.currentScreenWidth,
    });
    this.playerInfoBar.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.playerContainer.addChild(this.playerInfoBar);

    // Timer border (traces playerInfoBar perimeter)
    this.timerBorder = new TimerBorder({
      screenWidth: this.currentScreenWidth,
    });
    this.timerBorder.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.timerBorder.visible = false;
    this.playerContainer.addChild(this.timerBorder);

    // Winner badge (hidden by default)
    this.winnerBadge = new Container();
    this.winnerBadge.label = 'winnerBadge';
    this.winnerBadge.visible = false;
    this.createWinnerBadge();
    this.playerContainer.addChild(this.winnerBadge);

    // Last action label (hidden by default)
    this.lastActContainer = new Container();
    this.lastActContainer.label = 'lastActLabel';
    this.lastActContainer.visible = false;

    this.lastActBg = new Graphics();
    this.lastActContainer.addChild(this.lastActBg);

    this.lastActLabel = new Text({
      text: '',
      style: {
        fill: 0xffffff,
        fontSize: this.bbs(SEAT_BREAKPOINTS.badgeFont),
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    this.lastActLabel.anchor.set(0.5);
    this.lastActContainer.addChild(this.lastActLabel);

    this.lastActContainer.y = this.bbs(SEAT_BREAKPOINTS.lastActY);
    this.playerContainer.addChild(this.lastActContainer);

    // Sitout badge with countdown
    this.sitoutBadge = new SitoutBadge({
      screenWidth: this.currentScreenWidth,
    });
    this.sitoutBadge.y = this.bbs(SEAT_BREAKPOINTS.lastActY);
    this.sitoutBadge.visible = false;
    this.playerContainer.addChild(this.sitoutBadge);

    // Start hidden until a player is set
    this.seatContainer.visible = false;

    // Origin debug crosshair for seat container
    addOriginMarker(this.seatContainer, ORIGIN_COLORS.heroSeat);

    // =========================================================================
    // CARDS CONTAINER (hero cards + hand meter)
    // =========================================================================
    this.cardsContainer = new Container();
    this.cardsContainer.label = 'heroCardsContainer';

    this.holeCardsView = new HoleCardsView('hero');
    this.cardsContainer.addChild(this.holeCardsView);

    this.handStrengthMeter = new HandStrengthMeter();
    this.cardsContainer.addChild(this.handStrengthMeter);

    // Origin debug crosshairs (absolute screen coords)
    addOriginMarker(this.holeCardsView, ORIGIN_COLORS.heroCards);
    addOriginMarker(this.handStrengthMeter, ORIGIN_COLORS.heroMeter);

    // =========================================================================
    // SUBSCRIBE TO GAME EVENTS (hand meter logic)
    // =========================================================================
    gameController.on('holeCardsDealt', this.onHoleCardsDealt);
    gameController.on('streetChanged', this.onStreetChanged);
    gameController.on('handStarted', this.onHandStarted);
    storageService.on('change', this.onStorageChange);
  }

  // ===========================================================================
  // PUBLIC API — PLAYER STATE
  // ===========================================================================

  /**
   * Set the hero player for this seat
   */
  setPlayer(player: PlayerState | null): void {
    this.player = player;

    if (player) {
      this.showPlayer(player);
      this.seatContainer.visible = true;
    } else {
      this.seatContainer.visible = false;
      this.clearAvatarSprite();
    }
  }

  /**
   * Get current player state
   */
  getPlayer(): PlayerState | null {
    return this.player;
  }

  /**
   * Set whether this seat is active (hero's turn)
   */
  setActive(active: boolean): void {
    this.isActive = active;

    if (active) {
      const geo = this.playerInfoBar.getGeometry();
      this.timerBorder.updateGeometry(geo.width, geo.height, geo.cornerRadius);
      this.timerBorder.setProgress(1.0, true);
      this.timerBorder.show();
    } else {
      this.timerBorder.hide();
    }
  }

  /**
   * Update timer ring progress
   */
  setTimerProgress(progress: number): void {
    if (!this.isActive) return;

    const geo = this.playerInfoBar.getGeometry();
    this.timerBorder.updateGeometry(geo.width, geo.height, geo.cornerRadius);
    this.timerBorder.setProgress(progress);
  }

  /**
   * Set the sitout countdown timer
   */
  setSitoutCountdown(seconds: number | null): void {
    this.sitoutBadge.setCountdown(seconds);
  }

  // ===========================================================================
  // PUBLIC API — CARDS & METER
  // ===========================================================================

  /** Get the hole cards view (for deal/fold/show/muck operations) */
  getHoleCardsView(): HoleCardsView {
    return this.holeCardsView;
  }

  /** Get the hand strength meter */
  getHandStrengthMeter(): HandStrengthMeter {
    return this.handStrengthMeter;
  }

  /** T71: Set reference to community cards view for draw prediction overlays */
  setCommunityCardsView(view: CommunityCardsView): void {
    this.communityCardsViewRef = view;
  }

  // ===========================================================================
  // PUBLIC API — EFFECT INJECTION POINTS
  // ===========================================================================

  /** Get bet chips container (for ChipMoveEffect) */
  getBetChipsContainer(): Container {
    return this.playerInfoBar.getBetChipsContainer();
  }

  /** Get winner badge (for PlayerWinEffect) */
  getWinnerBadge(): Container {
    return this.winnerBadge;
  }

  /** Get timer border (for effect access) */
  getTimerBorder(): TimerBorder {
    return this.timerBorder;
  }

  /** Get avatar container (for avatar effects) */
  getAvatarContainer(): Container {
    return this.avatarContainer;
  }

  /** Get avatar glow (for player aura effects) */
  getAvatarGlow(): Graphics {
    return this.avatarGlow;
  }

  /** Get seat position (seatContainer position for effect targeting) */
  getPosition(): { x: number; y: number } {
    return { x: this.seatContainer.x, y: this.seatContainer.y };
  }

  /** Get player chips display (for direct controller updates) */
  getPlayerChipsDisplay(): PlayerChipsDisplay {
    return this.playerInfoBar.getChipsDisplay();
  }

  /** Get player info bar (for direct access) */
  getPlayerInfoBar(): PlayerInfoBar {
    return this.playerInfoBar;
  }

  // ===========================================================================
  // PUBLIC API — POSITIONING & LAYOUT
  // ===========================================================================

  /**
   * Position the entire hero cockpit from a single anchor point.
   * Seat is placed at anchor + HERO_SEAT_OFFSET (BBS). Cards are placed at
   * seat + HERO_CARDS_OFFSET (BBS). Both use smooth BBS interpolation.
   */
  setCockpitPosition(anchorX: number, anchorY: number, screenWidth: number, _screenHeight: number): void {
    // Seat at grid position + hero offset (BBS pixels)
    const seatOffsetX = getScaledValue(screenWidth, HERO_SEAT_OFFSET.x, { discrete: false });
    const seatOffsetY = getScaledValue(screenWidth, HERO_SEAT_OFFSET.y, { discrete: false });
    const seatX = anchorX + seatOffsetX;
    const seatY = anchorY + seatOffsetY;
    this.seatContainer.x = seatX;
    this.seatContainer.y = seatY;

    // Cards offset from seat (BBS pixels)
    const cardsOffsetX = getScaledValue(screenWidth, HERO_CARDS_OFFSET.x, { discrete: false });
    const cardsOffsetY = getScaledValue(screenWidth, HERO_CARDS_OFFSET.y, { discrete: false });
    this.holeCardsView.x = seatX + cardsOffsetX;
    this.holeCardsView.y = seatY + cardsOffsetY;
    this.updateMeterPosition();
  }

  /**
   * Update responsive dimensions for current screen width
   */
  resize(screenWidth: number): void {
    if (screenWidth === this.currentScreenWidth) return;
    this.currentScreenWidth = screenWidth;

    // Update text styles
    this.lastActLabel.style.fontSize = this.bbs(SEAT_BREAKPOINTS.badgeFont);

    // Update sub-components
    this.playerInfoBar.resize(screenWidth);
    this.timerBorder.resize(screenWidth);
    this.sitoutBadge.resize(screenWidth);

    // Update positions
    this.playerInfoBar.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.timerBorder.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);
    this.sitoutBadge.y = this.bbs(SEAT_BREAKPOINTS.lastActY);
    this.lastActContainer.y = this.bbs(SEAT_BREAKPOINTS.lastActY);

    // Sync timer border geometry
    const geo = this.playerInfoBar.getGeometry();
    this.timerBorder.updateGeometry(geo.width, geo.height, geo.cornerRadius);

    // Recreate winner badge with new sizes
    this.createWinnerBadge();

    // Update avatar if player present
    if (this.player) {
      this.drawAvatar(this.player);
      this.updateAvatarSpriteSize(this.bbs(SEAT_BREAKPOINTS.avatarSize));
    }

    // Update cards + meter responsive scaling
    this.holeCardsView.updateLayout(screenWidth);
    this.handStrengthMeter.updateLayout(screenWidth);
    this.updateMeterPosition();
  }

  // ===========================================================================
  // CLEANUP
  // ===========================================================================

  destroy(): void {
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Unsubscribe from game events
    gameController.off('holeCardsDealt', this.onHoleCardsDealt);
    gameController.off('streetChanged', this.onStreetChanged);
    gameController.off('handStarted', this.onHandStarted);
    storageService.off('change', this.onStorageChange);

    // Clean up sprites
    this.clearAvatarSprite();

    // Destroy containers and all children
    this.seatContainer.destroy({ children: true });
    this.cardsContainer.destroy({ children: true });
  }

  // ===========================================================================
  // PRIVATE — RENDERING
  // ===========================================================================

  private showPlayer(player: PlayerState): void {
    this.playerContainer.visible = true;
    this.avatarContainer.visible = true;
    this.playerInfoBar.visible = true;

    // Hero avatar is never clickable (no profile popup for self)
    this.avatarContainer.eventMode = 'passive';
    this.avatarContainer.cursor = 'default';

    // Draw avatar
    this.drawAvatar(player);

    // Update player info bar
    this.playerInfoBar.setName(player.profile.name);
    const shouldAnimate = this.playerInfoBar.getChipsDisplay().getChips() > 0;
    this.playerInfoBar.setChips(player.stack, shouldAnimate);
    this.playerInfoBar.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY);

    // Show sitout badge if sitting out
    this.sitoutBadge.visible = player.status === 'sitout';

    // Update last action display
    this.updateLastAction(player.lastAction);
  }

  private drawAvatar(_player: PlayerState): void {
    const size = this.bbs(SEAT_BREAKPOINTS.avatarSize);

    // Hero always uses AvatarService for avatar ID
    const avatarId = avatarService.getAvatarId() ?? DEFAULT_AVATAR_ID;
    if (avatarId !== this.currentAvatarId) {
      this.currentAvatarId = avatarId;
      void this.loadAvatarSprite(avatarId, size);
    } else if (this.avatarSprite) {
      this.updateAvatarSpriteSize(size);
    }

    // Hero fold: alpha stays 1.0 (cards are dimmed instead)
    this.seatContainer.alpha = 1;
  }

  private async loadAvatarSprite(avatarId: number, size: number): Promise<void> {
    const generation = ++this.avatarLoadGeneration;
    const path = getAvatarPath(avatarId);

    try {
      const texture = await Assets.load<Texture>(path);

      if (generation !== this.avatarLoadGeneration || this.isDestroyed) return;

      // Create or update shadow sprite
      if (!this.avatarShadowSprite) {
        this.avatarShadowSprite = new Sprite(texture);
        this.avatarShadowSprite.label = 'avatarShadow';
        this.avatarShadowSprite.anchor.set(0.5);
        this.avatarShadowSprite.y = -10;
        this.avatarShadowSprite.tint = 0x000000;
        this.avatarShadowSprite.alpha = 0.55;
        this.avatarShadowSprite.filters = [new BlurFilter({ strength: 4 })];
        this.avatarContainer.addChild(this.avatarShadowSprite);
      } else {
        this.avatarShadowSprite.texture = texture;
      }

      // Create or update main sprite
      if (!this.avatarSprite) {
        this.avatarSprite = new Sprite(texture);
        this.avatarSprite.label = 'avatarSprite';
        this.avatarSprite.anchor.set(0.5);
        this.avatarSprite.y = -10;
        this.avatarContainer.addChild(this.avatarSprite);
      } else {
        this.avatarSprite.texture = texture;
      }

      this.updateAvatarSpriteSize(size);
    } catch {
      if (generation === this.avatarLoadGeneration) {
        this.clearAvatarSprite();
      }
    }
  }

  private clearAvatarSprite(): void {
    this.currentAvatarId = null;

    if (this.avatarShadowSprite) {
      this.avatarContainer.removeChild(this.avatarShadowSprite);
      this.avatarShadowSprite.destroy();
      this.avatarShadowSprite = null;
    }

    if (this.avatarSprite) {
      this.avatarContainer.removeChild(this.avatarSprite);
      this.avatarSprite.destroy();
      this.avatarSprite = null;
    }
  }

  private updateAvatarSpriteSize(size: number): void {
    const shadowOffset = size * 0.06;
    if (this.avatarShadowSprite) {
      this.avatarShadowSprite.width = size;
      this.avatarShadowSprite.height = size;
      this.avatarShadowSprite.y = -10 + shadowOffset;
    }
    if (this.avatarSprite) {
      this.avatarSprite.width = size;
      this.avatarSprite.height = size;
      this.avatarSprite.visible = true;
    }
  }

  // ===========================================================================
  // PRIVATE — BADGES & LABELS
  // ===========================================================================

  private updateLastAction(action: PlayerState['lastAction'] | undefined): void {
    if (!action) {
      this.lastActContainer.visible = false;
      return;
    }

    const actionConfig = this.getActionDisplay(action);
    this.lastActLabel.text = actionConfig.text;
    this.lastActLabel.style.fill = COLORS.lastAction.textFill;

    const padding = COLORS.lastAction.padding;
    const bgWidth = this.lastActLabel.width + padding.x * 2;
    const bgHeight = this.lastActLabel.height + padding.y * 2;

    this.lastActBg.clear();
    this.lastActBg.roundRect(-bgWidth / 2, -bgHeight / 2, bgWidth, bgHeight, COLORS.lastAction.cornerRadius);
    this.lastActBg.fill(actionConfig.color);

    this.lastActContainer.visible = true;
  }

  private getActionDisplay(action: string): { text: string; color: number } {
    switch (action) {
      case 'fold':
        return { text: 'FOLD', color: COLORS.lastAction.fold };
      case 'check':
        return { text: 'CHECK', color: COLORS.lastAction.check };
      case 'call':
        return { text: 'CALL', color: COLORS.lastAction.call };
      case 'bet':
        return { text: 'BET', color: COLORS.lastAction.bet };
      case 'raise':
        return { text: 'RAISE', color: COLORS.lastAction.raise };
      case 'allin':
        return { text: 'ALL IN', color: COLORS.lastAction.allin };
      default:
        return { text: action.toUpperCase(), color: COLORS.lastAction.default };
    }
  }

  private createWinnerBadge(): void {
    this.winnerBadge.removeChildren();

    const badgeText = new Text({
      text: 'WINNER',
      style: {
        fill: COLORS.winnerBadge.textFill,
        fontSize: this.bbs(SEAT_BREAKPOINTS.badgeFont) + 2,
        fontFamily: 'Roboto, Arial, sans-serif',
        fontWeight: 'bold',
      },
    });
    badgeText.anchor.set(0.5);

    const padding = COLORS.winnerBadge.padding;
    const badgeWidth = badgeText.width + padding * 2;
    const badgeHeight = badgeText.height + padding * 2;

    const badgeBg = new Graphics();
    badgeBg.roundRect(-badgeWidth / 2, -badgeHeight / 2, badgeWidth, badgeHeight, 4);
    badgeBg.fill(COLORS.winnerBadge.fill);

    this.winnerBadge.addChild(badgeBg);
    this.winnerBadge.addChild(badgeText);

    this.winnerBadge.x = 0;
    this.winnerBadge.y = this.bbs(SEAT_BREAKPOINTS.playerInfoBarY) + 40;
  }

  // ===========================================================================
  // PRIVATE — DRAW PREDICTION (T71)
  // ===========================================================================

  private updateDrawPredictions(street: Street): void {
    if (!this.communityCardsViewRef) return;

    // Only show overlays on flop and turn (empty slots exist for predictions)
    if (street !== 'flop' && street !== 'turn') {
      this.communityCardsViewRef.clearDrawPredictions();
      return;
    }

    const heroCards = tableState.heroCards.get();
    if (!heroCards) {
      this.communityCardsViewRef.clearDrawPredictions();
      return;
    }

    const board = tableState.communityCards.get();
    const draws = detectDraws(heroCards, board);
    console.log(
      '[HeroView] updateDrawPredictions:',
      `street=${street}`,
      `heroCards=${heroCards.map((c) => `${c.rank}${c.suit}`).join(',')}`,
      `board=${board.map((c) => `${c.rank}${c.suit}`).join(',')}`,
      `draws=${draws.length}`,
      draws.map((d) => `${d.drawName}(${String(d.outs)})`).join(', '),
    );
    this.communityCardsViewRef.showDrawPredictions(draws, street);
  }

  // ===========================================================================
  // PRIVATE — HAND STRENGTH METER POSITIONING
  // ===========================================================================

  private updateMeterPosition(): void {
    const offsetX = getScaledValue(this.currentScreenWidth, HAND_STRENGTH_METER_OFFSET.x, { discrete: false });
    const offsetY = getScaledValue(this.currentScreenWidth, HAND_STRENGTH_METER_OFFSET.y, { discrete: false });

    this.handStrengthMeter.x = this.seatContainer.x + offsetX;
    this.handStrengthMeter.y = this.seatContainer.y + offsetY;
    this.handStrengthMeter.updateLayout(this.currentScreenWidth);
  }
}
